import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckType } from '@/backend/features/background-check/types';
import { BackgroundCheckAccountDetails } from '@/ui/features/background-check/components/background-check-modal/components/BackgroundCheckAccountDetails';
import { BackgroundCheckModalConfirmButton } from '@/ui/features/background-check/components/background-check-modal/components/BackgroundCheckModalConfirmButton';
import { ConsentAlert } from '@/ui/features/background-check/components/background-check-modal/components/ConsentAlert';
import { ManualBackgroundCheckStatusSelect } from '@/ui/features/background-check/components/background-check-modal/components/ManualBackgroundCheckStatusSelect';
import { RequiredInfoAlert } from '@/ui/features/background-check/components/background-check-modal/components/RequiredInfoAlert';
import { useAccountValidator } from '@/ui/features/background-check/components/background-check-modal/hooks/useAccountValidator';
import { formatDateTime } from '@juvare/common-web-ui/utils';
import { AccountData } from '@juvare/contact-management/backend/features/accounts/shared';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { Alert, Button, DialogActions, DialogContent, DialogTitle, Divider } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

export interface BackgroundCheckModalProps {
    account: AccountData;
    backgroundCheck: BackgroundCheckColumns;
    accountId: string;
    onCloseModal: (closed?: () => void) => void;
    ariaTitleId: string;
    ariaContentId: string;
    onReloadAccounts?: () => void;
}

export const BackgroundCheckModal: React.FC<BackgroundCheckModalProps> = ({
    account,
    backgroundCheck,
    accountId,
    onCloseModal,
    ariaTitleId,
    ariaContentId,
    onReloadAccounts,
}) => {
    const { t } = useTranslation();

    const isConsentGiven = Boolean(backgroundCheck.consent);
    const [backgroundCheckStatus, setBackgroundCheckStatus] = React.useState(backgroundCheck.status);

    const { backgroundCheckConfig } = useSelector(getSettings);
    const backgroundCheckType = backgroundCheck.type as BackgroundCheckType;
    const backgroundCheckConfigByType = backgroundCheckConfig.type[backgroundCheckType];

    const { validationResults, validationErrors, errorFieldsLabels } = useAccountValidator(account, backgroundCheckType);
    const isDataValid = validationResults.success;

    const requiresConsent = backgroundCheckConfigByType.requiresConsent;
    const requiresValidation = backgroundCheckConfigByType.requiresFormValidation;
    const backgroundCheckProcess = backgroundCheckConfigByType.process;

    return (
        <>
            <DialogTitle id={ariaTitleId}>
                {isConsentGiven && isDataValid
                    ? t('backgroundCheck.labels.submitBackgroundCheck')
                    : t('backgroundCheck.labels.contactVolunteer')}
            </DialogTitle>
            <DialogContent id={ariaContentId}>
                {(!isConsentGiven || !isDataValid) && <p>{t('backgroundCheck.messages.contactVolunteerInfo')}</p>}
                {requiresConsent && <ConsentAlert isConsentGiven={isConsentGiven} />}
                <Divider sx={{ mt: 2, mb: !requiresConsent ? 2 : 0 }} />
                {requiresConsent && <RequiredInfoAlert isDataValid={isDataValid} />}
                <BackgroundCheckAccountDetails
                    account={account}
                    type={backgroundCheckType}
                    validationErrors={validationErrors}
                    showValidationErrors={requiresValidation}
                />
                {account?.lastContactedAt && (
                    <Alert severity='info' sx={{ mt: 2 }}>
                        {`${t('backgroundCheck.messages.lastContacted')}: ${formatDateTime(new Date(account.lastContactedAt))}`}
                    </Alert>
                )}
                <ManualBackgroundCheckStatusSelect
                    backgroundCheckStatus={backgroundCheckStatus}
                    backgroundCheckType={backgroundCheckType}
                    backgroundCheckProcess={backgroundCheckProcess}
                    setBackgroundCheckStatus={setBackgroundCheckStatus}
                    isFormValid={isDataValid || !requiresValidation}
                />
            </DialogContent>
            <DialogActions>
                <Button color='primary' onClick={() => onCloseModal()} data-test='close-dialog-button'>
                    {t('common.cancel')}
                </Button>
                <BackgroundCheckModalConfirmButton
                    accountId={accountId}
                    backgroundCheckType={backgroundCheckType}
                    backgroundCheckStatus={backgroundCheckStatus}
                    backgroundCheckProcess={backgroundCheckProcess}
                    missingProfileInformation={errorFieldsLabels}
                    onCloseModal={onCloseModal}
                    onReloadAccounts={onReloadAccounts}
                    requiresValidation={requiresValidation}
                    isFormValid={isDataValid}
                />
            </DialogActions>
        </>
    );
};
